/**
 * Core Character interfaces for Rogue Trader RPG
 */

import type { Rank } from './advancements';
import type { CharacterTrait, Trait, TraitMechanics } from './traits';
import type { PsykerType, SustainedPower } from './psychic';
import type { NavigatorProfile } from './navigator';
import type { DamageType } from './dice';
import type { JamType } from './jam';

// Re-export Rank for convenience
export type { Rank } from './advancements';

/**
 * Where a portable item is, relative to the character. Ordered by readiness;
 * mutually exclusive, so illegal combinations are unrepresentable.
 *
 *  - `owned`   stored elsewhere (ship, stash). No encumbrance weight, not usable.
 *  - `carried` on the person (belt, sling, webbing). Counts weight; drawable.
 *  - `wielded` in hand or hand-equivalent. Attack-ready; consumes hand budget.
 *  - `dropped` on the ground in the current scene. Retrievable, weightless.
 *
 * Armour uses `worn` and cybernetics use `installed` instead — "wielded" is
 * meaningless for a breastplate and an implant is never carried.
 *
 * @see .kiro/specs/equipment-carry-state/design.md
 */
export type CarryState = 'owned' | 'carried' | 'wielded' | 'dropped';

/** The carry states that contribute weight for encumbrance purposes. */
export const ENCUMBERING_CARRY_STATES: readonly CarryState[] = ['carried', 'wielded'];

/** Character's dominant hand. Defaults to "Right" for backward compatibility. */
export type DominantHand = 'Right' | 'Left';

/** Per-weapon hand assignment during combat. Defaults to "unassigned". */
export type HandAssignment = 'dominant' | 'off-hand' | 'unassigned';

/**
 * Rank tracking for character advancement
 * Requirements: 5.1, 6.1, 6.2, 8.1
 */
export interface RankTracking {
  currentRank: Rank;
  highestRankAchieved: Rank;
  rankHistory: RankHistoryEntry[];
  activeCareerRank: Rank; // The rank player has selected (may differ from calculated)
  alternativeCareerTracking?: AlternativeCareerTracking;
  skippedRanks?: Rank[]; // Standard ranks that were skipped for alternative paths
}

/**
 * Entry in rank history tracking when a character achieves a new rank
 * Requirements: 5.4, 6.1, 6.2
 */
export interface RankHistoryEntry {
  rank: Rank;
  achievedAt: Date;
  spentXPAtAchievement: number;
  isAlternativeRank?: boolean;
  alternativeRankId?: string;
  alternativeRankName?: string;
}

/**
 * Tracking for alternative career path state
 * Requirements: 6.1, 6.2, 6.3
 */
export interface AlternativeCareerTracking {
  isOnAlternativePath: boolean;
  currentAlternativeRankId?: string; // ID of the alternative rank being used
  currentAlternativeRankName?: string; // Display name
  skippedStandardRank?: Rank; // The standard rank that was skipped
  returnRank?: Rank; // The rank to return to after completing alternative
}

/**
 * XP transaction log entry for tracking advancement purchases
 * Requirements: 9.1, 9.2
 */
export interface XPTransaction {
  id: string;
  type: 'advancement' | 'bonus' | 'manual';
  amount: number;
  description: string;
  timestamp: Date;
  resultingSpentXP: number;
  resultingRank: Rank;
}

export interface Characteristics {
  weaponSkill: number;
  ballisticSkill: number;
  strength: number;
  toughness: number;
  agility: number;
  intelligence: number;
  perception: number;
  willpower: number;
  fellowship: number;
  influence: number;
}

export interface ConditionalModifier {
  id: string;
  target: string; // e.g., "Willpower", "Commerce"
  value: number;
  condition: string; // e.g., "against corruption", "dealing with criminals"
  source: string; // e.g., "Birthright: Child of the Creed"
}

export interface RollMechanic {
  stat: 'Corruption' | 'Insanity' | 'Fate' | 'Profit Factor' | 'Thrones';
  dice: string; // e.g., "1d5", "1d10"
  multiplier?: number; // Optional multiplier for the result (e.g., 10 for "2d10 x 10")
  description?: string; // Optional override for the roll label
}

export interface StartingRoll {
  target: 'wounds' | 'fate' | 'insanity' | 'corruption' | 'profitFactor' | 'thrones';
  formula: string;
  bonus?: number;
  usesToughnessBonus?: boolean;
  fateTable?: Array<{ max: number; value: number }>;
  label: string;
}

export interface MechanicalEffect {
  description?: string; // Optional override or specific detail
  characteristics?: Partial<Record<keyof Characteristics, number>>;
  skills?: Partial<Record<string, number>>; // Skill name -> bonus
  talents?: string[]; // Talent names/IDs
  traits?: string[]; // Trait names/IDs
  xpBonus?: number;
  corruption?: number;
  insanity?: number;
  profitFactor?: number;
  fatePoints?: number;
  wounds?: number;
  conditionalModifiers?: ConditionalModifier[];
  rolls?: RollMechanic[];
  untrainedBasicSkills?: string[]; // Advanced skills that can be used untrained (e.g., Hive World's Caves of Steel grants Tech-Use)
  startingRolls?: StartingRoll[];
  specialRules?: string[]; // Non-mechanical benefits displayed as text (e.g., "Re-roll Fate Point wound recovery dice")
  effectChoice?: {
    description: string;
    options: Array<{
      id: string;
      name: string;
      description: string;
      type?: string;
      characteristics?: Partial<Record<keyof Characteristics, number>>;
      [key: string]: unknown;
    }>;
  };
}

export interface DerivedStats {
  wounds: number;
  fatigue: number;
  corruption: number;
  insanity: number;
  profitFactor: number;
  initiative: number;
  movementHalf: number;
  movementFull: number;
  movementCharge: number;
  movementRun: number;
}

export interface Experience {
  total: number;
  spent: number;
  available: number;
  transactions?: XPTransaction[];
}

/**
 * Record of a single skill advancement purchase.
 * Tracks when and how a skill was improved through the advancement system.
 *
 * Requirements: 3.1, 3.3, 5.5
 *
 * @example
 * {
 *   advancementId: "arch-militant-r1-dodge-plus-10",
 *   purchasedAt: new Date("2024-01-15"),
 *   rank: 1,
 *   bonus: 10
 * }
 */
export interface SkillPurchase {
  /**
   * Unique ID of the advancement that granted this skill improvement.
   * Uses kebab-case format encoding the career, rank, skill, and bonus level.
   * @example "arch-militant-r1-dodge-plus-10", "arch-militant-r2-dodge-plus-20"
   */
  advancementId: string;

  /**
   * Timestamp when this advancement was purchased.
   * Used for tracking character progression history.
   */
  purchasedAt: Date;

  /**
   * Career rank when this advancement was purchased.
   * Required for tracking progression and validating advancement availability.
   * @example 1, 2, 3
   */
  rank: number;

  /**
   * The bonus granted by this purchase: 10 or 20.
   * Represents the training level improvement (+10 for Trained, +20 for Experienced).
   * Note: The advancement ID also encodes this (e.g., "dodge-plus-10" vs "dodge-plus-20"),
   * but this field makes it explicit for calculations.
   * @example 10, 20
   */
  bonus: number;
}

/**
 * Represents a skill on a character sheet with complete data for self-contained calculations.
 * All fields needed for display and calculation are included - no external lookups required.
 *
 * Requirements: 5.1, 5.3, 5.5, 2.1, 2.2, 2.5
 *
 * @example
 * {
 *   id: "dodge",
 *   name: "Dodge",
 *   type: "Basic",
 *   characteristic: "agility",
 *   category: SkillCategory.Physical,
 *   training: SkillTraining.Trained,
 *   isSpecialist: false,
 *   rulebook: "rogue-trader-core",
 *   purchaseHistory: [
 *     { advancementId: "arch-militant-r1-dodge-plus-10", purchasedAt: new Date(), rank: 1, bonus: 10 }
 *   ]
 * }
 */
export interface Skill {
  /**
   * String ID from skills.json, used as the primary identifier.
   * Uses kebab-case format for consistency.
   * @example "acrobatics", "dodge", "common-lore-imperium"
   */
  id: string;

  /**
   * Display name shown in the UI.
   * @example "Acrobatics", "Dodge", "Common Lore (Imperium)"
   */
  name: string;

  /**
   * Skill type classification: 'Basic' or 'Advanced'.
   * Basic skills can be used untrained (at half characteristic),
   * Advanced skills require training.
   */
  type?: string;

  /**
   * Rulebook descriptor group printed alongside the skill name, e.g.
   * "Interaction", "Investigation", "Movement". A skill may carry more than one
   * as a comma-separated string. Used to identify Interaction Skill Tests.
   * @example "Interaction", "Crafting, Exploration"
   */
  descriptor?: string;

  /**
   * The characteristic this skill is based on for tests.
   * The skill test target number starts with this characteristic value.
   * @example "agility", "intelligence", "fellowship"
   */
  characteristic: keyof Characteristics;

  /**
   * Category for UI organization and filtering.
   * @example SkillCategory.Physical, SkillCategory.Social
   */
  category: SkillCategory;

  /**
   * Current training level, calculated from purchaseHistory.
   * Determines the bonus added to the characteristic for skill tests.
   * - Untrained: 0 (or -20 for Advanced skills)
   * - Trained: +10
   * - Experienced: +20
   */
  training: SkillTraining;

  /**
   * Whether this skill requires specialization (e.g., Common Lore, Forbidden Lore).
   * Specialist skills have multiple variants with different specializations.
   */
  isSpecialist: boolean;

  /**
   * Specific specialization if isSpecialist is true.
   * @example "Imperial Creed" for Common Lore, "Warp" for Forbidden Lore
   */
  specialization?: string;

  /**
   * Brief one-sentence summary suitable for hover tooltips.
   * Typically under 100 characters.
   */
  shortDescription?: string;

  /**
   * Complete description of the skill including what it does and how it works.
   */
  fullDescription?: string;

  /**
   * Source rulebook for this skill.
   * @example "rogue-trader-core", "into-the-storm"
   */
  rulebook: string;

  /**
   * For specialist skills, the base skill name without specialization.
   * Used for grouping and display purposes.
   * @example "Common Lore" for "Common Lore (Imperium)"
   */
  baseSkill?: string;

  /**
   * Whether this is a placeholder for user choice during character creation.
   * When true, the user must select a specific skill to replace this placeholder.
   */
  isChoosePlaceholder?: boolean;

  /**
   * Whether this skill has predefined specialization options to choose from.
   * When true, the UI should present a list of valid specializations.
   */
  hasEnumeratedOptions?: boolean;

  /**
   * Complete history of all advancement purchases for this skill.
   * Required field - initialized as empty array for new skills.
   * Used to calculate training level and track character progression.
   *
   * Note: Training bonus is calculated as MAX of purchase bonuses (not sum),
   * since +20 replaces +10, not adds to it.
   */
  purchaseHistory: SkillPurchase[];
}

export enum SkillCategory {
  Combat = 'combat',
  Social = 'social',
  Technical = 'technical',
  Mental = 'mental',
  Physical = 'physical',
  Knowledge = 'knowledge',
  Aptitude = 'aptitude'
}

export enum SkillTraining {
  Untrained = 'untrained',
  Trained = 'trained',
  Experienced = 'experienced',
  Adept = 'adept'
}

/**
 * Record of a single talent advancement purchase.
 * Tracks when and how a talent was acquired through the advancement system.
 *
 * Requirements: 3.2, 3.3, 4.2, 5.6
 *
 * @example
 * // Standard talent purchase
 * {
 *   advancementId: "rogue-trader-r2-air-of-authority",
 *   purchasedAt: new Date("2024-01-15"),
 *   rank: 2
 * }
 *
 * @example
 * // "Talented" talent purchase affecting a specific skill
 * {
 *   advancementId: "rogue-trader-r2-talented",
 *   purchasedAt: new Date("2024-01-15"),
 *   rank: 2,
 *   affectedSkillId: "commerce"
 * }
 */
export interface TalentPurchase {
  /**
   * Unique ID of the advancement that granted this talent.
   * Uses kebab-case format encoding the career, rank, and talent.
   * @example "rogue-trader-r2-talented", "arch-militant-r1-quick-draw"
   */
  advancementId: string;

  /**
   * Timestamp when this advancement was purchased.
   * Used for tracking character progression history.
   */
  purchasedAt: Date;

  /**
   * Career rank when this advancement was purchased.
   * Required for tracking progression and validating advancement availability.
   * @example 1, 2, 3
   */
  rank: number;

  /**
   * For "talented" talent - which skill this specific purchase affects.
   * This allows multiple "Talented" purchases to affect different skills.
   * Only used when the talent id is "talented".
   * @example "commerce", "command", "dodge"
   */
  affectedSkillId?: string;
}

/**
 * Represents a talent on a character sheet with complete data for self-contained calculations.
 * All fields needed for display and calculation are included - no external lookups required.
 *
 * Requirements: 5.2, 5.4, 5.6, 2.1, 2.2, 2.5, 4.2, 4.7
 *
 * @example
 * // Standard talent
 * {
 *   id: "air-of-authority",
 *   name: "Air of Authority",
 *   tier: 1,
 *   prerequisites: ["Fel 30"],
 *   description: "You have a commanding presence.",
 *   effect: "Affect more targets with Command tests.",
 *   rulebook: "rogue-trader-core",
 *   purchaseCount: 1,
 *   purchaseHistory: [
 *     { advancementId: "rogue-trader-r1-air-of-authority", purchasedAt: new Date(), rank: 1 }
 *   ]
 * }
 *
 * @example
 * // "Talented" talent with affected skill
 * {
 *   id: "talented",
 *   name: "Talented",
 *   tier: 1,
 *   prerequisites: [],
 *   description: "You have a natural aptitude for a skill.",
 *   effect: "+10 bonus to one skill of your choice.",
 *   rulebook: "rogue-trader-core",
 *   purchaseCount: 1,
 *   purchaseHistory: [
 *     { advancementId: "rogue-trader-r2-talented", purchasedAt: new Date(), rank: 2, affectedSkillId: "commerce" }
 *   ],
 *   affectedSkillId: "commerce"
 * }
 */
export interface Talent {
  /**
   * String ID from talents.json, used as the primary identifier.
   * Uses kebab-case format for consistency.
   * @example "air-of-authority", "talented", "weapon-training-las"
   */
  id: string;

  /**
   * Display name shown in the UI.
   * @example "Air of Authority", "Talented", "Weapon Training (Las)"
   */
  name: string;

  /**
   * Talent tier (1, 2, or 3) - indicates power level and XP cost category.
   * Higher tiers are more powerful but cost more XP.
   * @example 1, 2, 3
   */
  tier: number;

  /**
   * Array of prerequisite strings that must be met to acquire this talent.
   * Can include characteristic requirements, other talents, or skills.
   * @example ["Fel 30"], ["WP 40", "Strong Minded"], []
   */
  prerequisites: string[];

  /**
   * Brief one-sentence summary suitable for hover tooltips.
   * Typically under 100 characters.
   * @example "Affect more targets with Command/Interaction Tests."
   */
  shortDescription: string;

  /**
   * Complete description of the talent including what it does and how it works.
   * Contains full rules text and mechanical benefits.
   * @example "The Explorer was born to command, either motivating or terrifying those under his charge..."
   */
  fullDescription: string;

  /**
   * Collection of specializations available for talents that can be taken multiple times.
   * Only present for talents like Weapon Training, Peer, Hatred, etc.
   * @example ["Bolt", "Las", "Melta", "Plasma"] for Weapon Training
   */
  talentGroups?: string[];

  /**
   * Source rulebook for this talent.
   * @example "rogue-trader-core", "into-the-storm"
   */
  rulebook: string;

  /**
   * For specialized talents, the base talent name without specialization.
   * Used for grouping and display purposes.
   * @example "Weapon Training" for "Weapon Training (Las)"
   */
  baseTalent?: string;

  /**
   * Specific specialization if applicable.
   * @example "Las" for Weapon Training, "Orks" for Hatred
   */
  specialization?: string;

  /**
   * Whether this is a placeholder for user choice during character creation.
   * When true, the user must select a specific talent to replace this placeholder.
   */
  isChoosePlaceholder?: boolean;

  /**
   * Whether this talent has predefined specialization options to choose from.
   * When true, the UI should present a list of valid specializations.
   */
  hasEnumeratedOptions?: boolean;

  /**
   * Whether this talent can be purchased multiple times with different specializations.
   * @example true for Weapon Training (can have Las, Bolt, etc.)
   */
  allowMultiplePurchases?: boolean;

  /**
   * Total number of times this talent has been purchased.
   * Required field - initialized to 0 for new talents, incremented on each purchase.
   * For talents with specializations, this counts all purchases regardless of specialization.
   */
  purchaseCount: number;

  /**
   * Complete history of all advancement purchases for this talent.
   * Required field - initialized as empty array for new talents.
   * Used to track character progression and validate advancement availability.
   */
  purchaseHistory: TalentPurchase[];

  /**
   * Where the talent came from during character creation or advancement.
   * Helps track the source for display and validation purposes.
   * @example "Career: Rogue Trader - Rank 2", "Homeworld: Void Born", "Advancement"
   */
  source?: string;

  /**
   * For "talented" talent - which skill gets +10 bonus.
   * Only used when talent id is "talented" and has a single purchase.
   * For multiple "Talented" purchases, each purchase in purchaseHistory has its own affectedSkillId.
   * @example "commerce", "command", "dodge"
   */
  affectedSkillId?: string;
}

export type { CharacterTrait, Trait, TraitMechanics };

/**
 * Categories of equipment determining how items are used and displayed.
 * Each type has specific handling in the UI and game mechanics.
 */
export enum EquipmentType {
  /** Combat weapons (melee and ranged) - see Weapon interface */
  Weapon = 'weapon',
  /** Protective gear covering body locations - see Armor interface */
  Armor = 'armor',
  /** General equipment and tools */
  Gear = 'gear',
  /** Cybernetic implants and augmentations */
  Cybernetic = 'cybernetic',
  /** Consumable substances with temporary effects */
  Drug = 'drug',
  /** Specialized tools for specific tasks */
  Tool = 'tool'
}

/**
 * Quality level of equipment affecting its stats and value.
 * - Poor: -10 to relevant tests, reduced effectiveness
 * - Common: Standard quality, no modifiers
 * - Good: +5 to relevant tests, improved effectiveness
 * - Best: +10 to relevant tests, maximum effectiveness
 */
export type Craftsmanship = 'Poor' | 'Common' | 'Good' | 'Best';

/**
 * Base interface for all equipment items owned by a character.
 * Equipment uses string-based identification exclusively - no numeric IDs.
 *
 * The `id` field is a unique instance identifier for this specific equipment item
 * in the character's inventory, while `sourceId` references the item definition
 * in items.json.
 *
 * @example
 * ```typescript
 * const laspistol: Equipment = {
 *   id: "char-weapon-001",           // Unique instance ID
 *   sourceId: "laspistol",           // References items.json entry
 *   type: EquipmentType.Weapon,
 *   weight: 1.5,
 *   availability: "Common",
 *   cost: 50,
 *   description: "A reliable las weapon",
 *   rulebook: "rogue-trader-core",
 *   equipped: true,
 *   craftsmanship: "Common"
 * };
 * ```
 */
export interface Equipment {
  /**
   * Unique instance identifier for this specific equipment item.
   * Generated when the item is added to a character's inventory.
   * Used to distinguish between multiple copies of the same item type.
   * @example "char-weapon-001", "inv-armor-003"
   */
  id: string;

  /**
   * String ID referencing the item definition in items.json.
   * Uses kebab-case format matching the source data file.
   * This is the primary key for looking up item properties.
   * @example "laspistol", "flak-armor", "micro-bead", "enforcer-light-carapace"
   */
  sourceId: string;

  /**
   * Category of equipment determining how it's used and displayed.
   * @see EquipmentType enum for valid values
   */
  type: EquipmentType;

  /**
   * Weight of the item in kilograms.
   * Used for encumbrance calculations.
   */
  weight: number;

  /**
   * Availability rating indicating how easy the item is to find.
   * Affects acquisition tests and shop availability.
   * @example "Ubiquitous", "Abundant", "Plentiful", "Common", "Average", "Scarce", "Rare", "Very Rare", "Extremely Rare", "Near Unique"
   */
  availability: string;

  /**
   * Base cost in Thrones (Imperial currency).
   * Optional as some items may be priceless or have variable costs.
   */
  cost?: number;

  /**
   * Text description of the item's appearance and function.
   */
  description: string;

  /**
   * Source rulebook where this item is defined.
   * Uses kebab-case format.
   * @example "rogue-trader-core", "into-the-storm", "hostile-acquisitions"
   */
  rulebook: string;

  /**
   * Where this item is relative to the character.
   *
   * Replaces the former `equipped` boolean, which eleven call sites read with
   * three different meanings (attackable / worn / installed). Required, because
   * an item always has a location.
   *
   * For armour, this is the *location* only — whether it is donned is `Armor.worn`,
   * so armour in a pack is `carried` but not worn. For cybernetics, see `installed`.
   */
  carryState: CarryState;

  /**
   * Whether a cybernetic is fitted to the body. Only meaningful when
   * `type === EquipmentType.Cybernetic`.
   *
   * Installed cybernetics provide their benefits and contribute no encumbrance
   * weight — they are part of the body, not a load. An uninstalled spare is
   * ordinary equipment with a `carryState`.
   */
  installed?: boolean;

  /**
   * Quality level of the item affecting its stats and effectiveness.
   * @see Craftsmanship type for valid values and their effects
   */
  craftsmanship?: Craftsmanship;

  /**
   * Body location where a cybernetic is installed.
   * Only meaningful for items with type === EquipmentType.Cybernetic.
   * Accepts standard BodyLocation values or free-text strings.
   */
  installedLocation?: string;

  /**
   * Player-assigned custom name for this item instance.
   * @example "Excalibur" for a mono-blade, "Old Faithful" for a laspistol
   */
  customName?: string;

  /**
   * Player-written notes about this item: provenance, condition, lore, etc.
   * @example "Found in the ruins of Hive Tarsus. Blade edge is chipped but still sharp."
   */
  customDescription?: string;

  /**
   * How many are held, for consumables that are expended on use.
   *
   * Grenades and narcotics are bought and spent in quantity, so they stack onto one
   * entry rather than creating one per unit. Absent means a single, non-consumable item.
   */
  quantity?: number;
}

/**
 * Weapon equipment with combat-specific properties.
 * Extends the base Equipment interface with weapon statistics.
 *
 * @example
 * ```typescript
 * const boltPistol: Weapon = {
 *   id: "char-weapon-002",
 *   sourceId: "bolt-pistol",
 *   type: EquipmentType.Weapon,
 *   weight: 3.5,
 *   availability: "Rare",
 *   description: "A powerful bolt weapon",
 *   rulebook: "rogue-trader-core",
 *   class: WeaponClass.Pistol,
 *   range: 30,
 *   rateOfFire: { single: 1, semiAuto: 2, fullAuto: 0 },
 *   damage: "1d10+5",
 *   penetration: 4,
 *   clip: 8,
 *   reload: "Full",
 *   special: ["Tearing"],
 *   proficiencyRequired: "Bolt"
 * };
 * ```
 */
export interface Weapon extends Equipment {
  /**
   * Weapon class determining how it's used in combat.
   * @see WeaponClass enum for valid values
   */
  class: WeaponClass;

  /**
   * Maximum effective range in meters.
   * Melee weapons typically have range 0 or use special rules.
   */
  range: number;

  /**
   * Rate of fire options for the weapon.
   * Determines how many shots can be fired per action type.
   */
  rateOfFire: RateOfFire;

  /**
   * Damage formula in dice notation.
   * @example "1d10+3", "2d10", "1d5+2"
   */
  damage: string;

  /** Damage type (energy/impact/explosive/rending). Optional; defaults to Impact when unset. */
  damageType?: DamageType;

  /**
   * Armor penetration value.
   * Reduces target's armor by this amount when calculating damage.
   */
  penetration: number;

  /**
   * Ammunition capacity before reload is required.
   * 0 for melee weapons or weapons that don't use ammunition.
   */
  clip: number;

  /**
   * Action type required to reload the weapon.
   * @example "Half", "Full", "2 Full"
   */
  reload: string;

  /**
   * Array of special weapon qualities and rules.
   * @example ["Tearing", "Reliable", "Accurate"]
   */
  special: string[];

  /**
   * Quality level affecting weapon stats.
   * Overrides base Equipment craftsmanship for weapon-specific effects.
   */
  craftsmanship?: Craftsmanship;

  /**
   * Current ammunition remaining in the clip.
   * Undefined means full (for backwards compatibility).
   */
  currentClip?: number;

  /**
   * Currently loaded ammo type (e.g. "Standard", "Man-Stopper", "Inferno").
   * Undefined means standard ammunition.
   */
  ammoType?: string;

  /**
   * Reload rounds remaining. 0 or undefined = not reloading.
   * Decremented each round during multi-round reloads.
   */
  reloadRoundsLeft?: number;

  /**
   * Recharge rounds remaining. 0 or undefined = ready to fire.
   * Set after firing a Recharge weapon.
   */
  rechargeRoundsLeft?: number;

  /**
   * Persisted jam state. Cleared only by the clearing action described by
   * `JamService.getClearingAction`, never by navigation or a tab switch.
   *
   * Survives stow and drop: a jammed weapon on the floor is still jammed.
   * A jammed weapon remains wieldable but cannot fire.
   */
  jamState?: { jammed: boolean; type: JamType };

  /**
   * Number of Full Actions required to reload. Parsed from reload string.
   * 0 = Half Action, 1 = Full Action, 2+ = multi-round.
   */
  reloadActions?: number;

  /**
   * Number of rounds to recharge after firing. 0 or undefined = no recharge.
   * Derived from Recharge or Recharge(X) special quality.
   */
  rechargeTime?: number;

  /**
   * Parsed special rule parameters. Maps rule name to numeric parameter.
   * @example { "Blast": 3, "Toxic": 1, "Proven": 3, "Felling": 4 }
   */
  specialParams?: Record<string, number>;

  /**
   * Array of modifications applied to the weapon.
   * @example ["Mono", "Red-Dot Laser Sight", "Silencer"]
   */
  modifications?: string[];

  /**
   * Weapon proficiency category required to use without penalty.
   * Characters without the matching Weapon Training talent suffer -20 to hit.
   * @example "Las", "Bolt", "Melta", "Plasma", "Exotic", "Power", "Chain", "Flame"
   */
  proficiencyRequired?: string;

  /** Which hand holds this weapon during combat. Defaults to "unassigned". */
  handAssignment?: HandAssignment;

  /**
   * ID of the currently loaded unusual ammunition type.
   * References AmmunitionDefinition.id from the ammunition registry.
   * `undefined` or empty string (`''`) means standard ammunition is loaded.
   *
   * Note: The existing `ammoType?: string` field above is a legacy display-only
   * field and is NOT used by the unusual ammunition system.
   */
  loadedAmmoId?: string;

  /**
   * Marks this weapon as a synthetic, in-memory-only weapon (e.g. Luminen Blast/Shock).
   * Synthetic weapons MUST be filtered out of inventory listings and persistence.
   * See `.kiro/specs/luminen-powers/design.md` for context.
   */
  isSynthetic?: boolean;
}

/**
 * Weapon class categories determining combat usage rules.
 */
export enum WeaponClass {
  /** Close combat weapons */
  Melee = 'melee',
  /** One-handed ranged weapons, can be used in melee */
  Pistol = 'pistol',
  /** Two-handed ranged weapons requiring both hands */
  Basic = 'basic',
  /** Large weapons often requiring bracing or mounting */
  Heavy = 'heavy',
  /** Weapons designed to be thrown */
  Thrown = 'thrown'
}

/**
 * Rate of fire options for ranged weapons.
 * Values indicate number of shots per action type.
 * 0 means that fire mode is not available.
 */
export interface RateOfFire {
  /** Shots fired with Single Shot action */
  single: number;
  /** Shots fired with Semi-Auto Burst action */
  semiAuto: number;
  /** Shots fired with Full Auto Burst action */
  fullAuto: number;
}

/**
 * Armor equipment providing protection to body locations.
 * Extends the base Equipment interface with armor statistics.
 *
 * @example
 * ```typescript
 * const flakArmor: Armor = {
 *   id: "char-armor-001",
 *   sourceId: "flak-armor",
 *   type: EquipmentType.Armor,
 *   weight: 11,
 *   availability: "Average",
 *   description: "Standard Imperial Guard armor",
 *   rulebook: "rogue-trader-core",
 *   locations: [BodyLocation.Body, BodyLocation.LeftArm, BodyLocation.RightArm],
 *   armorPoints: 4,
 *   special: [],
 *   equipped: true
 * };
 * ```
 */
export interface Armor extends Equipment {
  /**
   * Whether the armour is donned. Orthogonal to `carryState`: worn armour is
   * necessarily carried, but carried armour may sit unworn in a pack, adding
   * weight without providing AP.
   *
   * Only worn armour contributes armour points.
   */
  worn: boolean;

  /**
   * Body locations protected by this armor.
   * @see BodyLocation enum for valid values
   */
  locations: BodyLocation[];

  /**
   * Armor points (AP) provided to protected locations.
   * Reduces incoming damage by this amount.
   */
  armorPoints: number;

  /**
   * Array of special armor qualities and rules.
   * @example ["Primitive", "Environmentally Sealed"]
   */
  special: string[];

  /**
   * Quality level affecting armor stats.
   * Overrides base Equipment craftsmanship for armor-specific effects.
   */
  craftsmanship?: Craftsmanship;
}

/**
 * Body locations that can be protected by armor.
 * Used for hit location determination and armor coverage.
 */
/**
 * The six body locations, used for armour coverage, hit location and injury.
 *
 * SINGLE SOURCE OF TRUTH. Three identical declarations previously existed - an
 * `BodyLocation` enum here, an identical `BodyLocation` enum in types/armor.ts,
 * and a `BodyLocation` string union further down this file. That is how a plain
 * string could reach an enum-typed field, which is what let items.json ship
 * "arms" and "legs" into character armour unchecked.
 *
 * Declared as a const object rather than an enum so string literals remain
 * assignable: a TS string enum rejects `'head'`, and the codebase passes raw
 * literals in many places where that is perfectly readable.
 *
 * Cybernetic INSTALLATION sites are a different, wider set - see
 * CyberneticInstallLocation in services/CyberneticsService.ts.
 */
export const BodyLocation = {
  Head: 'head',
  LeftArm: 'leftArm',
  RightArm: 'rightArm',
  Body: 'body',
  LeftLeg: 'leftLeg',
  RightLeg: 'rightLeg'
} as const;

export type BodyLocation = (typeof BodyLocation)[keyof typeof BodyLocation];

/** Every valid location, for validation and iteration. */
export const BODY_LOCATIONS = Object.values(BodyLocation) as readonly BodyLocation[];

export interface ManualAdjustment {
  id: string;
  field: string;
  oldValue: unknown;
  newValue: unknown;
  reason: string;
  timestamp: Date;
}

// Progression Tracking Types

export interface Currency {
  thrones: number; // Standard Imperial currency
}

export interface MentalDisorder {
  id: string;
  type: 'phobia' | 'obsession' | 'visions' | 'delusion' | 'nightmares' | 'flesh-is-weak';
  name: string;
  description: string;
  severity: 'minor' | 'severe' | 'acute';
  acquiredAtIP: number;
}

export type PendingTestType =
  | 'trauma-test'
  | 'malignancy-test'
  | 'mutation-test'
  | 'disorder-assignment';

export interface PendingTest {
  id: string;
  type: PendingTestType;
  threshold: number;
  severity?: 'minor' | 'severe' | 'acute';
  createdAt: number;
}

export interface StoredMalignancy {
  id: string;
  name: string;
  description: string;
  effect: Record<string, unknown>;
  acquiredAtCP: number;
  tableRoll: number;
}

export interface StoredMutation {
  id: string;
  name: string;
  description: string;
  effect: Record<string, unknown>;
  acquiredAtCP: number;
  tableRoll: number;
}

export interface MentalState {
  insanityPoints: number;
  corruptionPoints: number;
  insanityThreshold: number; // Typically 100
  corruptionThreshold: number; // Typically 100
  disorders: MentalDisorder[];
  /** Characteristics already used for Corruption mutation tests (can't reuse) */
  usedMutationTestCharacteristics?: string[];
  pendingTests?: PendingTest[];
  storedMalignancies?: StoredMalignancy[];
  storedMutations?: StoredMutation[];
}

export interface Mutation {
  id: string;
  name: string;
  description: string;
  effect: string;
  severity: 'minor' | 'major' | 'grave';
  acquiredAt: Date;
  corruptionWhenAcquired: number;
}

export interface MutationTracking {
  mutations: Mutation[];
  totalMutations: number;
  lastMutationCheck: number; // Corruption level at last check
}

export interface PsychicPower {
  id: string;
  name: string;
  discipline: string;
  psyRating: number;
  description: string;
  effect: string;
  rulebook: string;
}

export interface PsychicProfile {
  isPsyker: boolean;
  psyRating: number;
  psykerType: PsykerType;
  powers: PsychicPower[];
  phenomena: string[]; // History of psychic phenomena
  sustainedPowers: SustainedPower[];
}

export interface FatePoints {
  current: number;
  maximum: number;
  destiny: number; // Separate from fate points
}

export interface LocationDamage {
  location: BodyLocation;
  currentDamage: number;
  criticalEffects: string[]; // Active critical effects on this location
}

export interface CriticalDamage {
  id: string;
  location: BodyLocation;
  severity: number; // 1-10+ based on damage over threshold
  effect: string;
  description: string;
  isPermanent: boolean; // Amputations, permanent disabilities
  timestamp: Date;
}

export interface PermanentInjury {
  id: string;
  name: string;
  location: BodyLocation;
  description: string;
  mechanicalEffect: string; // e.g., "-10 to Agility tests"
  acquiredAt: Date;
  canBeHealed: boolean;
}

export interface WoundsTracking {
  current: number;
  maximum: number; // Calculated from characteristics
  totalDamage: number; // Total damage taken (maximum - current)
  locationDamage: LocationDamage[]; // Damage by body location
  criticalDamage: CriticalDamage[]; // Active critical damage effects
  permanentInjuries: PermanentInjury[]; // Permanent injuries (amputations, etc.)
  unconscious: boolean; // True when wounds reach 0
  dead: boolean; // True when wounds go significantly negative
  firstAidApplied: boolean; // True after First Aid test on current injuries; resets on new damage
}

export interface FatigueTracking {
  current: number;
  maximum: number; // Calculated from characteristics
}

export interface MovementRates {
  half: number; // Calculated from Agility Bonus
  full: number; // 2x Half
  charge: number; // 3x Half
  run: number; // 6x Half
}

export interface ProgressionTracking {
  currency: Currency;
  mentalState: MentalState;
  mutationTracking: MutationTracking;
  psychicProfile: PsychicProfile;
  navigatorProfile?: NavigatorProfile;
  fatePoints: FatePoints;
  woundsTracking: WoundsTracking;
  fatigueTracking: FatigueTracking;
  movementRates: MovementRates;
  prosanguineDisabledUntilHour?: number; // game-time hour (timePassage.totalElapsedHours) at which Autosanguine/Prosanguine re-enable; undefined/null means active
}

export interface CombatPreferences {
  favouritedActions: string[]; // Array of action IDs that are favourited
  aimBonus: number; // 0 = no aim, 10 = half action aim, 20 = full action aim
}

// Character creation choice interfaces - records what options were selected during creation

/** Shared base for all effect-related sub-choice state across origin path steps */
export interface EffectChoices {
  effectChoice?: string;
  effectCharacteristicChoices?: string[];
  effectCharacteristicDrawbackChoices?: string[];
  effectSkillChoices?: string[];
  effectTalentChoice?: string;
  effectTalentChoices?: string[];
  effectDrawbackChoice?: string;
  effectBenefitChoices?: Array<{ type: 'skill' | 'talent'; id: string }>;
  effectSkillUpgrades?: string[];
  effectAlternativePath?: 'train' | 'upgrade';
}

export interface BirthrightChoices extends EffectChoices {
  talentChoice?: string;
  characteristicChoice?: keyof Characteristics;
  drawbackChoice?: 'corruption' | 'insanity';
  benefitChoice?: { type: 'skill' | 'talent'; id: string };
}

export interface LureChoices extends EffectChoices {
  subChoice?: string;
  characteristicChoice?: string;
  skillChoice?: string;
  talentChoice?: string;
  benefitChoice?: { type: 'skill' | 'talent'; id: string };
}

export interface MotivationChoices extends EffectChoices {
  talentChoice?: string;
  talentSpecialization?: string;
  heirloomRoll?: number;
  heirloomChoice?: string;
}

export interface TrialChoices extends EffectChoices {
  benefitChoice?: { type: 'skill' | 'talent'; id: string };
  talentChoice?: string;
  skillChoice?: string;
  skillSpecialization?: string;
  enemyChoice?: string;
  effectSkillChoice?: string;
}

export interface LineageChoices extends EffectChoices {
  characteristicChoice?: keyof Characteristics;
  skillChoice?: string;
  skillChoices?: string[];
  talentChoice?: string;
  talentChoices?: string[];
  rollChoice?: string; // for corruption/insanity choice rolls
  psychicDiscipline?: string;
  psychicTechnique?: string;
}

export interface EquipmentChoices {
  weapon1?: string; // ID of selected weapon1 option
  weapon2?: string; // ID of selected weapon2 option
  armor?: string; // ID of selected armor option
  gear?: string; // ID of selected gear option
  clothing?: string; // ID of selected clothing option
}

export interface Character {
  id: string;
  name: string;
  career: Career;
  homeworld: Homeworld;
  birthright: Birthright;
  lure: LureOfTheVoid;
  trials: Trials;
  motivation: Motivation;

  characteristics: Characteristics;
  derivedStats: DerivedStats;
  experience: Experience;

  skills: Skill[];
  talents: Talent[];
  traits: Trait[];
  equipment: Equipment[];
  weapons: Weapon[];
  armor: Armor[];

  // Progression tracking
  progression?: ProgressionTracking;

  // Rank tracking for advancement system
  rankTracking?: RankTracking;

  // Combat preferences
  combatPreferences?: CombatPreferences;

  // Character creation choices - records what options were selected
  homeworldBonusCharacteristic?: keyof Characteristics;
  homeworldTalentChoices?: string[];
  homeworldSkillChoices?: string[];
  birthrightChoices?: BirthrightChoices;
  lureChoices?: LureChoices;
  motivationChoices?: MotivationChoices;
  /** Optional lineage selection (ITS Ch01 — 7th Origin Path row) */
  lineage?: { id: string; name: string; optionId: string; optionName: string; xpCost: number };
  lineageChoices?: LineageChoices;
  equipmentChoices?: EquipmentChoices;
  /** Career-specific creation choices (Weapon Master class, Void-Master mastery, Explorator bionics) */
  careerChoices?: {
    archMilitantWeaponClass?: string;
    voidMasterMastery?: string;
    exploratorCybernetics?: string[];
    exploratorCyberneticUpgrades?: Record<string, number>;
  };

  enabledRulebooks: string[];
  manualAdjustments: ManualAdjustment[];

  // Active conditional modifiers
  conditionalModifiers: ConditionalModifier[];

  // Metadata
  schemaVersion: number;
  createdAt: Date;
  lastModified: Date;

  // Forge World specific
  initialWoundsBonus?: number;

  /** Character's dominant hand. Defaults to "Right" when undefined. */
  dominantHand?: DominantHand;

  /**
   * Inventory of unusual ammunition quantities.
   * Keyed by ammunition definition ID (e.g., "man-stopper-bullets").
   * Value is the number of rounds available.
   */
  ammoInventory?: Record<string, number>;
}

// Equipment choice option for character creation
export interface EquipmentChoiceOption {
  id: string;
  quality?: 'poor' | 'common' | 'good' | 'best';
  upgrade?: string; // e.g., 'mono' for mono upgrade
  quantity?: number;
  note?: string;
}

// Equipment choices structure for careers
export interface StartingEquipmentChoices {
  weapon1?: EquipmentChoiceOption[];
  weapon2?: EquipmentChoiceOption[];
  armor?: EquipmentChoiceOption[];
  gear?: EquipmentChoiceOption[];
  clothing?: EquipmentChoiceOption[];
}

// Forward declarations for character creation interfaces
export interface Career {
  id: string;
  name: string;
  description: string;
  startingSkills: string[];
  startingTalents: string[];
  startingTraits?: string[];
  startingEquipment: string[];
  /**
   * @deprecated Use startingEquipment with string IDs instead. This field will be removed in a future version.
   */
  startingEquipmentIds?: string[];
  startingEquipmentChoices?: StartingEquipmentChoices;
  startingCompanions?: Array<{ type: string; note?: string }>;
  startingTalentNotes?: Record<string, string>;
  aptitudes: string[];
  rulebook: string;
}

export interface Homeworld {
  id: string;
  name: string;
  description: string;
  characteristicModifiers: Partial<Characteristics>;
  startingSkills: string[];
  startingTalents: string[];
  startingTraits: string[];
  startingEquipment: string[];
  /**
   * @deprecated Use startingEquipment with string IDs instead. This field will be removed in a future version.
   */
  startingEquipmentIds?: string[];
  rulebook: string;
  fitForPurpose?: boolean;

  mechanics?: MechanicalEffect;
  talentChoices?: Array<{
    description: string;
    options: string[];
  }>;
  skillChoices?: Array<{
    description: string;
    options: string[];
  }>;
}

export interface Birthright {
  id: string;
  name: string;
  description: string;
  benefit: string;
  mechanics?: MechanicalEffect;
  startingEquipment?: string[];
  /**
   * @deprecated Use startingEquipment with string IDs instead. This field will be removed in a future version.
   */
  startingEquipmentIds?: string[];
  rulebook: string;
}

export interface LureOfTheVoid {
  id: string;
  name: string;
  description: string;
  benefit: string;
  mechanics?: MechanicalEffect;
  rulebook: string;
}

export interface Trials {
  id: string;
  name: string;
  description: string;
  benefit: string;
  mechanics?: MechanicalEffect;
  drawback?: string;
  drawbackMechanics?: MechanicalEffect;
  rulebook: string;
}

export interface Motivation {
  id: string;
  name: string;
  description: string;
  benefit: string;
  mechanics?: MechanicalEffect;
  rulebook: string;
}
